import { useSyncExternalStore } from 'react';

/*
    How much of the app fits across the window.

    The two numbers are taken from the app's own limits rather than picked. The
    resizer already refuses to make the reading pane narrower than 420px or the
    message list narrower than 280 (see Mailbox), and the sidebar is 254 wide:

      254 + 280 + 420 = 954  — below this, three panes cannot all be usable
      254 +       420 = 674  — below this, the sidebar and one readable message
                               cannot sit side by side

    Rounded to 960 and 680. Change the resizer's clamps and these should move
    with them; they describe the same judgement about what is readable.

    Measured in JavaScript rather than CSS because every layout decision in this
    app is an inline style chosen by a branch — `flexDirection`, `display: none`,
    which element is rendered at all. A media query cannot reach any of those,
    and the one that exists (print, in global.css) is the exception that proves
    it: it works by hiding things, not by choosing a layout.
*/

/** Below this the message replaces the list instead of sharing the width with it. */
export const PANES_FIT = 960;
/** Below this the sidebar becomes a drawer instead of holding a column of its own. */
export const SIDEBAR_FITS = 680;

export interface Panes {
    /** The reading pane cannot sit beside the list: show one at a time. */
    onePane: boolean;
    /** The sidebar cannot hold a column: open it over the screen instead. */
    drawerNav: boolean;
}

/** The whole rule, in one place, so a test can ask it directly. */
export function panesFor(width: number): Panes {
    return {
        onePane: width < PANES_FIT,
        drawerNav: width < SIDEBAR_FITS,
    };
}

/*
    The snapshot is cached deliberately.

    useSyncExternalStore compares snapshots by identity and re-renders when they
    differ, so a function returning a fresh object every call would re-render on
    every resize event — sixty times a second while somebody drags the window
    edge, to deliver two booleans that change twice. Holding the last object and
    returning it unchanged means the subscription can be noisy and the app still
    only re-renders when a breakpoint is actually crossed.
*/
let cached: Panes = { onePane: false, drawerNav: false };

function currentPanes(): Panes {
    const next = panesFor(window.innerWidth);
    if (next.onePane !== cached.onePane || next.drawerNav !== cached.drawerNav) cached = next;
    return cached;
}

function subscribe(onChange: () => void): () => void {
    window.addEventListener('resize', onChange);
    // Rotating a phone does not always fire resize before the new size is readable.
    window.addEventListener('orientationchange', onChange);
    return () => {
        window.removeEventListener('resize', onChange);
        window.removeEventListener('orientationchange', onChange);
    };
}

/** Which panes fit right now. Re-renders only when that answer changes. */
export function usePanes(): Panes {
    return useSyncExternalStore(subscribe, currentPanes);
}

/*
    The live numbers, for the few places that need the size itself rather than
    which layout it implies — the composer clamps itself to the window, and a
    clamp computed once at render is wrong the moment the window changes.
*/
export function useWindowWidth(): number {
    return useSyncExternalStore(subscribe, () => window.innerWidth);
}

export function useWindowHeight(): number {
    return useSyncExternalStore(subscribe, () => window.innerHeight);
}
